import React, { lazy, memo, Suspense, useCallback, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectActiveChartId } from '../../../selectors/chart'
import { AppContext } from '../../../App'
import { DrawingTypes } from '../drawingTools/drawing.interface'
import { ChartEvents } from '../chart.events'

// Fetched the first time a drawing gets focus rather than with the entry bundle.
const ChartDrawingMiniSettings = lazy(() => import('./chartDrawingMiniSettings'))
const ChartDrawingSettingsModal = lazy(() => import('./chartDrawingSettingsModal'))

export type ChartDrawingSettingsProps = {
    chartId: string
}

const ChartDrawingSettings: React.FC<ChartDrawingSettingsProps> = ({ chartId }) => {
    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)
    const [focusedDrawingId, setFocusedDrawingId] = useState<DrawingTypes | null>(null)
    const [fullSettingsOpen, setFullSettingsOpen] = useState(false)
    const onDrawingFocused = useCallback((event: CustomEvent) => {
        const { id, focused } = event.detail
        if (focused) {
            setFocusedDrawingId(id)
        } else if (id === focusedDrawingId) {
            setFocusedDrawingId(null)
        }
    }, [focusedDrawingId])

    const onOpenDrawingSettings = useCallback((event: CustomEvent) => {
        const { open } = event.detail
        setFullSettingsOpen(open)
    }, [])

    const onDrawingDestroyed = useCallback((event: CustomEvent) => {
        const { id: drawingId } = event.detail
        if (drawingId === focusedDrawingId) {
            setFocusedDrawingId(null)
        }
    }, [focusedDrawingId])

    useEffect(() => {
        appContext.getChart(chartId)?.subscribeEvent(ChartEvents.DRAWING_FOCUSED, onDrawingFocused)
        appContext.getChart(chartId)?.subscribeEvent(ChartEvents.OPEN_DRAWING_SETTINGS, onOpenDrawingSettings)
        appContext.getChart(chartId)?.subscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
        return () => {
            appContext.getChart(chartId)?.unsubscribeEvent(ChartEvents.DRAWING_FOCUSED, onDrawingFocused)
            appContext.getChart(chartId)?.unsubscribeEvent(ChartEvents.OPEN_DRAWING_SETTINGS, onOpenDrawingSettings)
            appContext.getChart(chartId)?.unsubscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
        }
    }, [onDrawingFocused])

    useEffect(() => {
        appContext.getChart(chartId)?.subscribeEvent(ChartEvents.OPEN_DRAWING_SETTINGS, onOpenDrawingSettings)
        return () => {
            appContext.getChart(chartId)?.unsubscribeEvent(ChartEvents.OPEN_DRAWING_SETTINGS, onOpenDrawingSettings)
        }
    }, [])

    if (chartId !== activeChartId || !focusedDrawingId) return null
    return (<Suspense fallback={null}>
            {!fullSettingsOpen &&
                <ChartDrawingMiniSettings id={focusedDrawingId} openFullSettings={() => setFullSettingsOpen(true)} />}
            {fullSettingsOpen &&
                <ChartDrawingSettingsModal id={focusedDrawingId} onClose={() => setFullSettingsOpen(false)} />}
        </Suspense>
    )
}

export default memo(ChartDrawingSettings)
